import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import {
    DIVIDE_CANCEL_LABEL,
    DIVIDE_FEWER_LABEL,
    DIVIDE_MORE_LABEL,
    DIVIDE_MOVE_LABEL,
    DIVIDE_QUANTITY_LABEL,
    DIVIDE_TITLE,
} from '../constants';
import { PromptStepper, PromptText } from './blocks';

/** The typed quantity as a whole number, or null when it isn't one (blank, decimal, or not a number). */
const parseQuantity = (value) => (/^\d+$/.test(String(value).trim()) ? Number(value) : null);

/**
 * The "How many to move?" prompt shown when a card that can be divided is moved to another place. The input takes a
 * whole number from 1 to the card's quantity and starts at the full quantity; − and + step it within that range.
 * Move (or Enter) hands the number to `onMove`; Cancel, Escape, or a click outside calls `onCancel`. The caller
 * places focus after it closes. Give it a `key` per card, so each opening starts from that card's quantity.
 *
 * @param {{ pending: { quantity: number, lineNumber: number, item: { sku?: string, description?: string }|null, fromLabel: string, toLabel: string }|null, onMove: (quantity: number) => void, onCancel: () => void, disabled?: boolean }} props
 *   `pending` is the modal's `pendingMove`; the prompt is open while it is set. `disabled` locks Move, for example
 *   while a split is submitting.
 */
export const DividePrompt = ({ pending, onMove, onCancel, disabled = false }) => {
    const [value, setValue] = useState(() => String(pending?.quantity ?? ''));
    const max = pending?.quantity ?? 1;
    const amount = parseQuantity(value);
    const valid = amount !== null && amount >= 1 && amount <= max;
    const name = [pending?.item?.sku, pending?.item?.description].filter(Boolean).join(' · ');

    const step = (delta) => setValue(String(Math.min(max, Math.max(1, (amount ?? 0) + delta))));

    const handleSubmit = (event) => {
        event.preventDefault();
        if (valid && !disabled) onMove(amount);
    };

    return (
        <Dialog
            open={!!pending}
            onClose={onCancel}
            maxWidth="xs"
            fullWidth
            disableRestoreFocus
            aria-labelledby="split-divide-title"
        >
            <form onSubmit={handleSubmit} noValidate>
                <DialogTitle id="split-divide-title">{DIVIDE_TITLE}</DialogTitle>
                <DialogContent>
                    <PromptText>
                        Line {pending?.lineNumber}
                        {name ? ` · ${name}` : ''}
                    </PromptText>
                    <PromptText>
                        {max} in {pending?.fromLabel} → {pending?.toLabel}
                    </PromptText>
                    <PromptStepper>
                        <IconButton
                            size="small"
                            aria-label={DIVIDE_FEWER_LABEL}
                            disabled={amount !== null && amount <= 1}
                            onClick={() => step(-1)}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                            type="number"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            error={!valid}
                            helperText={valid ? `of ${max}` : `Enter a whole number from 1 to ${max}`}
                            autoFocus
                            variant="outlined"
                            size="small"
                            inputProps={{ min: 1, max, step: 1, 'aria-label': DIVIDE_QUANTITY_LABEL }}
                        />
                        <IconButton
                            size="small"
                            aria-label={DIVIDE_MORE_LABEL}
                            disabled={amount !== null && amount >= max}
                            onClick={() => step(1)}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </PromptStepper>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton type="button" onClick={onCancel}>
                        {DIVIDE_CANCEL_LABEL}
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={!valid || disabled}>
                        {DIVIDE_MOVE_LABEL}
                    </PrimaryButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DividePrompt;
